import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useState } from "react";

export function GenericPieChart({
  title = "Pie Chart",
  subtitle = "",
  data = [],
  dataKey = "value",
  nameKey = "name",
  colors = ["#3b82f6", "#60a5fa", "#93c5fd"],
  innerRadius = "30%",
  outerRadius = "80%",
  centerLabel = null,
  showLegend = true,
  tooltipFormatter = (value) => value,
  tooltipTitle = "Value",
  tooltipSubtitle = "Details",
  showHeader = true,
  border = true,
  showSummary = true, // New prop to toggle summary
  startAngle = 360,
  endAngle = 0
}) {
  const [showLegendTooltip, setShowLegendTooltip] = useState(false);

  // Calculate total for percentage
  const total = data.reduce((sum, item) => sum + item[dataKey], 0);

  // Custom label component with both name and percentage
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    value,
    name,
    payload,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Only show label if segment is big enough (more than 3%)
    if (percent < 0.03) return null;

    return (
      <g>
        <text
          x={x}
          y={y}
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-xs font-medium"
        >
          {`${(percent * 100).toFixed(1)}%`}
        </text>
      </g>
    );
  };

  // Summary component
  const Summary = () => (
    <div className="grid grid-cols-3 gap-4 px-4 py-3 mt-auto border-t">
      {data.map((item, index) => (
        <div key={index} className="flex items-start gap-2">
          <div
            className="w-2 h-2 rounded-full mt-1"
            style={{
              backgroundColor: item.color || colors[index % colors.length],
            }}
          />
          <div>
            <div className="text-sm text-gray-600">{item[nameKey]}</div>
            <div className="text-sm font-medium text-gray-900">
              {tooltipFormatter(item[dataKey])}
            </div>
            <div className="text-xs text-gray-500">
              {((item[dataKey] / total) * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={` 
      h-full
       flex flex-col overflow-hidden bg-white rounded-lg ${
         border
           ? "shadow-sm border border-solid border-gray-300"
           : "border-transparent"
       }`}
    >
      {/* Header */}
      {showHeader && (
        <div className="p-3 shrink-0">
          <h3 className="summary-card__title">{title}</h3>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Chart Area */}
        <div className="flex-1 min-h-0 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy={startAngle === 180 ? "75%" : "50%"}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                paddingAngle={2}
                dataKey={dataKey}
                nameKey={nameKey}
                minAngle={15}
                label={renderCustomizedLabel}
                labelLine={false}
                startAngle={startAngle}
                endAngle={endAngle}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${entry[nameKey]}`}
                    fill={entry.color || colors[index % colors.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const percentage = ((data[dataKey] / total) * 100).toFixed(
                      1
                    );
                    return (
                      <div className="bg-white p-2 border border-gray-300 rounded-lg shadow-lg z-50">
                        <p className="text-xs font-medium text-gray-900 mb-1">
                          {data[nameKey]}
                        </p>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor:
                                data.color ||
                                colors[payload[0].payload.index || 0],
                            }}
                          ></div>
                          <p className="text-xs text-gray-700">
                            {tooltipFormatter(data[dataKey])} ({percentage}%)
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
                wrapperStyle={{ outline: "none", zIndex: 1000 }}
              />
            </PieChart>
          </ResponsiveContainer>
          {centerLabel && (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{
                top: startAngle === 180 ? "50%" : "0",
                transform: startAngle === 180 ? "translateY(25%)" : "none",
              }}
            >
              <div className="text-center">
                <div className="text-lg font-medium text-gray-900">
                  {centerLabel.value}
                </div>
                <div className="text-xs text-gray-500">{centerLabel.label}</div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Section */}
        {showSummary && <Summary />}
      </div>
    </div>
  );
}
